import {
  BayLevelEnum,
  IBayLevelData,
  IShipData,
  ISizeSummary,
  ISlotData,
  TContainerLengths,
} from "open-vessel-definition";
import {
  GRID_OPTIONS_BY_CELL_DIMENSION,
  GRID_OPTIONS_BY_CELL_GAP,
} from "./gridConfigConstants";
import {
  IBayPattern,
  ISlotCombinedPattern,
  ISlotPattern,
  createTiersFromConfig,
  getRowsAndTiersFromSlotKeys,
} from "@tedivo/tedivo-bay-grid-pure";
import {
  ICellPositions,
  ICreateGridFromConfigProps,
  INodesOptions,
  IReturnDrawFunction,
  createGridFromConfig,
} from "@tedivo/tedivo-bay-grid-core";
import {
  ILidDataWithValidation,
  IValidationOutput,
} from "@tedivo/tedivo-ovd-validators";
import {
  IntersectStatusEnum,
  createObserverWithCallback,
  removeChildren,
} from "@tedivo/tedivo-dom-helpers";

import CellSpacingEnum from "../../../../../app/enums/CellSpacingEnum";
import { EditDrawer } from "../../../../common/EditDrawer";
import SizeSmallMidLargeEnum from "../../../../../app/enums/SizeSmallMidLargeEnum";
import { TEditBayButtonFunction } from "../../render/view-json-render";
import { getHeaderDetailPills } from "./getHeaderDetailPills";
import globalUnits from "../../../../../app/units/globalUnits";
import { pad2 } from "@tedivo/tedivo-pure-helpers";

const EXTRA_SPACE_TOP = 60;

class BayBoxComponent extends HTMLElement {
  private bayBox: HTMLDivElement;
  private data: IData | undefined = undefined;

  private nodesOptions: INodesOptions | undefined = undefined;

  private createEditBayButtonFunction: TEditBayButtonFunction | undefined;

  private createFnSlotCell:
    | ((
      cellSize: SizeSmallMidLargeEnum,
    ) => (slotData: ISlotData) => IReturnDrawFunction)
    | undefined;

  private drawStackWeights:
    | ((props: {
      bayAbove: IBayLevelData | undefined;
      bayBelow: IBayLevelData | undefined;
      cellPositions: ICellPositions;
      cellSize?: SizeSmallMidLargeEnum;
      cellPadding?: CellSpacingEnum;
    }) => HTMLElement)
    | undefined;

  private _holderIntersectionStatus: IntersectStatusEnum;
  private _scale = 1;

  constructor() {
    super();

    this._holderIntersectionStatus = IntersectStatusEnum.Initialized;

    this.bayBox = document.createElement("div");
    this.bayBox.setAttribute("part", "box");

    this.attachShadow({ mode: "open" });
  }

  get holderIntersectionStatus() {
    return this._holderIntersectionStatus;
  }

  get scale() {
    return this._scale;
  }
  set scale(value: number) {
    if (value === this._scale) return;
    this._scale = value;
    this.setSize(this.data?.width || 0, this.data?.height || 0);
  }

  setSize(width: number, height: number): this {
    const bayBox = this.bayBox;
    const fullHeight = height + EXTRA_SPACE_TOP;

    // The box is drawn at its natural (unscaled) size and then visually scaled
    // down via `transform`. Scaling must be anchored at the top-left corner
    // (not the CSS default center) so the box stays put instead of sliding
    // down and to the right as it shrinks. `transform` doesn't shrink the
    // space the box reserves in the flex layout, so negative margins pull
    // that reserved footprint back down to match the visual (scaled) size.
    bayBox.style.width = `${width}px`;
    bayBox.style.height = `${fullHeight}px`;

    if (this._scale === 1) {
      bayBox.style.transform = ``;
      bayBox.style.transformOrigin = ``;
      bayBox.style.marginRight = ``;
      bayBox.style.marginBottom = ``;
    } else {
      bayBox.style.transform = `scale(${this._scale})`;
      bayBox.style.transformOrigin = `top left`;
      bayBox.style.marginRight = `${-(1 - this._scale) * width}px`;
      bayBox.style.marginBottom = `${-(1 - this._scale) * fullHeight}px`;
    }

    return this;
  }

  async setData(data: IData): Promise<this> {
    this.setAttribute("data-bay", data.isoBay);
    this.data = data;
    this.drawWithObserver(this.shadowRoot || null);

    return this;
  }

  setDrawingColors(nodesOptions: INodesOptions) {
    this.nodesOptions = nodesOptions;
    return this;
  }

  setCreateFnSlotCell(fn: BayBoxComponent["createFnSlotCell"]): this {
    this.createFnSlotCell = fn;
    return this;
  }

  setDrawingProps({
    createEditBayButtonFunction,
    createFnSlotCell,
    drawStackWeights,
  }: {
    createEditBayButtonFunction: TEditBayButtonFunction;
    createFnSlotCell: (
      cellSize: SizeSmallMidLargeEnum,
    ) => (slotData: ISlotData) => IReturnDrawFunction;
    drawStackWeights: (props: {
      bayAbove: IBayLevelData | undefined;
      bayBelow: IBayLevelData | undefined;
      cellPositions: ICellPositions;
      cellSize?: SizeSmallMidLargeEnum;
      cellPadding?: CellSpacingEnum;
    }) => HTMLElement;
  }) {
    this.createFnSlotCell = createFnSlotCell;
    this.createEditBayButtonFunction = createEditBayButtonFunction;
    this.drawStackWeights = drawStackWeights;
    return this;
  }

  /** Prepares the box to be drawn when visible */
  drawWithObserver(root: ShadowRoot | null) {
    if (!this.data?.isoBay || !root) {
      return;
    }

    const bayBox = this.bayBox;
    removeChildren(bayBox);

    if (
      this.holderIntersectionStatus ===
      IntersectStatusEnum.WaitingForIntersection
    ) {
      return;
    }

    this._holderIntersectionStatus = IntersectStatusEnum.WaitingForIntersection;

    const ioObserver = createObserverWithCallback(
      this.createGridOfBay(bayBox),
      { delay: 1 },
    );

    ioObserver.observe(bayBox);

    root.appendChild(bayBox);
  }

  updateData(newData: Partial<IData>): this {
    this.data = {
      ...(this.data as IData),
      ...newData,
    };

    if (this.shadowRoot) {
      removeChildren(this.bayBox);
      this.drawWithObserver(this.shadowRoot);
    }
    return this;
  }

  connectedCallback() {
    const shadowRoot = this.shadowRoot;
    this.drawWithObserver(shadowRoot);
  }

  private createGridOfBay = (bayBox: HTMLDivElement) => {
    return () => {
      if (!this.data) return;

      removeChildren(bayBox);

      const {
        sizeSummary,
        shipData,
        lidData,
        bays,
        isoBay,
        bayAbove,
        bayBelow,
        enabledCells,
        slotsDataAbove,
        slotsDataBelow,
        slotsData,
        width,
        height,
        ssMaxRow,
        cellSize,
        cellSeparation,
        validationResult,
        showBottomBases,
        showTCGs,
      } = this.data;

      this._holderIntersectionStatus = IntersectStatusEnum.Rendered;

      console.time(`createGridFromConfig ${isoBay}`);

      const top = document.createElement("div");
      top.setAttribute("part", "top");
      const h3 = document.createElement("h3");
      h3.setAttribute("part", "title");
      h3.innerHTML = bays.map((b, idx) => (idx === 0 ? b : `(${b})`)).join(" ");
      top.appendChild(h3);

      const editBtn = this.createEditBayButtonFunction?.(
        isoBay,
        sizeSummary,
        shipData,
        bayAbove,
        bayBelow,
        enabledCells,
        slotsDataAbove,
        slotsDataBelow,
      );

      if (editBtn) top.appendChild(editBtn);

      const detailsHolder = getHeaderDetailPills(bayAbove, bayBelow, validationResult);
      if (!detailsHolder.dataset.hasWarnings) {
        this.setAttribute("validation-result", "valid");
      } else {
        this.removeAttribute("validation-result");
      }

      bayBox.appendChild(top);
      bayBox.appendChild(detailsHolder);

      /** In case we need to show as disabled the 00 row column for some bays */
      const prohibitedRow00: ISlotCombinedPattern[] = sizeSummary.centerLineRow
        ? get00RowCells(
          sizeSummary,
          !(bayAbove === undefined || bayAbove?.centerLineRow),
          !(bayBelow === undefined || bayBelow?.centerLineRow),
        )
        : [];

      // Bottom Base: resolved per-bay (row override -> bay-common override -> ship-wide
      // master), and only for tiers that are actually a row's bottom tier in THIS bay.
      const bottomBaseLabelsByTier = showBottomBases
        ? buildBottomBaseLabelsByTier(bayAbove, bayBelow, shipData.masterCGs)
        : undefined;

      // TCG is per-row and per-level (above/below can legitimately differ), resolved
      // separately so the top ROW numbers show above-deck TCGs and the bottom ROW
      // numbers show below-deck TCGs.
      const aboveTcgLabelsByRow = showTCGs
        ? buildTcgLabelsByRow(bayAbove, shipData.masterCGs.aboveTcgs, ssMaxRow)
        : undefined;

      const belowTcgLabelsByRow = showTCGs
        ? buildTcgLabelsByRow(bayBelow, shipData.masterCGs.belowTcgs, ssMaxRow)
        : undefined;

      // gridNumbersFontSize/cgNumbersFontSize live in `nodesOptions`, not at the top
      // level, so they're pulled out of the per-cell-size config here instead of
      // being spread alongside it (where the grid renderer would silently ignore them).
      const { gridNumbersFontSize, cgNumbersFontSize, ...cellDimensionOptions } =
        GRID_OPTIONS_BY_CELL_DIMENSION[cellSize];

      const { svgGroup, symbols, cellPositions } =
        createGridFromConfig<ISlotData>({
          ...cellDimensionOptions,
          ...GRID_OPTIONS_BY_CELL_GAP[cellSeparation],
          ...this.data.sizeSummary,
          maxRow: ssMaxRow,
          centerLineRow:
            bayAbove?.centerLineRow || bayBelow?.centerLineRow ? 1 : 0,
          nodesOptions: { ...this.nodesOptions, gridNumbersFontSize, cgNumbersFontSize },
          enabledCells,
          prohibitedCells: prohibitedRow00,
          cellsDrawFunction: this.createFnSlotCell?.(cellSize),
          cellsToDraw: slotsData,
          lidData,
          symbolPrefix: "",
          showBottomBases,
          showTCGs,
          bottomBaseLabelsByTier,
          aboveTcgLabelsByRow,
          belowTcgLabelsByRow,
        });

      const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
      svg.setAttribute("xmlns", "http://www.w3.org/2000/svg");
      svg.setAttribute("width", `${width}px`);
      svg.setAttribute("height", `${height}px`);
      svg.setAttribute("viewBox", `0 0 ${width} ${height}`);
      svg.setAttribute("part", "svg-bay");
      svg.setAttribute("role", "figure");
      if (symbols)
        Object.keys(symbols).forEach((n) => svg.appendChild(symbols[n]));
      svg.appendChild(svgGroup);

      console.timeEnd(`createGridFromConfig ${isoBay}`);

      bayBox.appendChild(svg);

      if (cellSize === SizeSmallMidLargeEnum.XLARGE && this.drawStackWeights)
        bayBox.appendChild(
          this.drawStackWeights({
            bayAbove,
            bayBelow,
            cellPositions,
            cellSize,
            cellPadding: cellSeparation,
          }),
        );
    };
  };
}

export default BayBoxComponent;

customElements.define("bay-box-component", BayBoxComponent);

declare global {
  interface HTMLElementTagNameMap {
    "bay-box-component": BayBoxComponent;
  }
}

function get00RowCells(
  sizeSummary: ISizeSummary,
  aboveShouldHide00 = false,
  belowShouldHide00 = false,
): ISlotCombinedPattern[] {
  const tiers = [
    ...(aboveShouldHide00
      ? createTiersFromConfig(
        sizeSummary.minAboveTier,
        sizeSummary.maxAboveTier,
      )
      : []),
    ...(belowShouldHide00
      ? createTiersFromConfig(
        sizeSummary.minBelowTier,
        sizeSummary.maxBelowTier,
      )
      : []),
  ];
  return tiers.map((tier) => `00|${tier}` as ISlotCombinedPattern);
}

/** A bay level's slot keys that are genuinely usable: not restricted and with at least one size defined. */
function getEffectiveSlotKeys(bl: IBayLevelData | undefined): ISlotPattern[] {
  const perSlotInfo = bl?.perSlotInfo;
  if (!perSlotInfo) return [];

  return (Object.keys(perSlotInfo) as ISlotPattern[]).filter(
    (sk) => perSlotInfo[sk].restricted !== 1 && Object.keys(perSlotInfo[sk].sizes || {}).length > 0,
  );
}

/** Sort key matching `sortRowsArray`: port (even rows) sort before center ("00") before starboard (odd rows). */
function rowPortToStbdKey(row: string): number {
  return Number(row) % 2 === 1 ? Number(row) : -Number(row);
}

/**
 * Formats each row's own Bottom Base value (row override -> bay-common override ->
 * ship-wide master fallback) for display on the regular grid, keyed by tier. Only
 * emits a label for a tier when it's actually a row's bottom (lowest occupied) tier
 * in THIS bay -- tiers this bay doesn't use at all, or that no row starts from, are
 * left out, so e.g. a bay whose lowest tier is 12 never shows labels for 02-10.
 */
function buildBottomBaseLabelsByTier(
  bayAbove: IBayLevelData | undefined,
  bayBelow: IBayLevelData | undefined,
  masterCGs: IShipData["masterCGs"],
): Record<string, string> {
  const labels: Record<string, string> = {};

  [bayAbove, bayBelow].forEach((bl) => {
    const effectiveSlotKeys = getEffectiveSlotKeys(bl);
    if (effectiveSlotKeys.length === 0) return;

    const { rows, tiersByRow } = getRowsAndTiersFromSlotKeys(effectiveSlotKeys);

    rows.forEach((row) => {
      const bottomTier = pad2(tiersByRow[row].minTier);

      if (labels[bottomTier] !== undefined) return;

      const bottomBase =
        bl?.perRowInfo?.each?.[row]?.bottomBase ??
        bl?.perRowInfo?.common?.bottomBase ??
        masterCGs.bottomBases[bottomTier];

      if (bottomBase !== undefined)
        labels[bottomTier] = globalUnits.lengthUnits.fromValueToFormattedDisplay(
          globalUnits.vcgTransposer.fromValueToDisplay(bottomBase),
        );
    });
  });

  return labels;
}

/**
 * Formats a bay level's TCG values (per row) for display on the regular grid: row
 * override, falling back to that level's master TCGs (`masterCGs.aboveTcgs` for the
 * above-deck level, `masterCGs.belowTcgs` for the below-deck level). Only rows from
 * the port-most used row through the starboard-most used row are included; rows in
 * between are still shown even if empty/restricted, but rows outside that used range
 * are left out entirely.
 */
function buildTcgLabelsByRow(
  bayLevel: IBayLevelData | undefined,
  masterTcgsForLevel: Record<string, number>,
  maxRow: number,
): Record<string, string> {
  const labels: Record<string, string> = {};

  const usedRows = getRowsAndTiersFromSlotKeys(getEffectiveSlotKeys(bayLevel)).rows;
  if (usedRows.length === 0) return labels;

  const minKey = rowPortToStbdKey(usedRows[0]);
  const maxKey = rowPortToStbdKey(usedRows[usedRows.length - 1]);

  for (let row = 0; row <= maxRow; row += 1) {
    const isoRow = pad2(row);
    const key = rowPortToStbdKey(isoRow);
    if (key < minKey || key > maxKey) continue;

    const tcg = bayLevel?.perRowInfo?.each?.[isoRow]?.tcg ?? masterTcgsForLevel[isoRow];

    if (tcg !== undefined)
      labels[isoRow] = globalUnits.lengthUnits.fromValueToFormattedDisplay(
        globalUnits.tcgTransposer.fromValueToDisplay(tcg),
      );
  }

  return labels;
}

// async function createDataHash(data: IData): Promise<string> {
//   const { shipData, ...importantData } = data;

//   const d = flattenObject(importantData as unknown as Record<string, unknown>);
//   const fullStr = Object.keys(d)
//     .sort()
//     .map((k) => `${k}:${d[k]}`)
//     .join("|");

//   let hash = 0;
//   for (let i = 0, len = fullStr.length; i < len; i++) {
//     let chr = fullStr.charCodeAt(i);
//     hash = (hash << 5) - hash + chr;
//     hash |= 0; // Convert to 32bit integer
//   }

//   return String(hash);
// }

interface IData {
  sizeSummary: ISizeSummary;
  shipData: IShipData;
  lidData: Array<ILidDataWithValidation>;
  bays: IBayPattern[];
  isoBay: IBayPattern;
  bayAbove: IBayLevelData | undefined;
  bayBelow: IBayLevelData | undefined;
  enabledCells: ISlotCombinedPattern[];
  slotsDataAbove: ISlotData[];
  slotsDataBelow: ISlotData[];
  slotsData: ISlotData[];
  width: number;
  height: number;
  ssMaxRow: number;
  cellSize: SizeSmallMidLargeEnum;
  cellSeparation: CellSpacingEnum;
  validationResult: IValidationOutput<unknown>[];
  showBottomBases: boolean;
  showTCGs: boolean;
}

export interface IBayEditProps {
  isoBay: IBayPattern;
  level: BayLevelEnum;
  availableLengths: Array<TContainerLengths>;
  bayData: IBayLevelData | undefined;
  sizeSummary: ISizeSummary;
  shipData: IShipData;
  gridData: ICreateGridFromConfigProps<ISlotData>;
  editDrawer?: EditDrawer;
  readOnlyMode: boolean;
  cellSize: SizeSmallMidLargeEnum;
}
